import { Portal } from '@chakra-ui/react';
import {
  INoteReqSchema,
  INoteResSchema,
  NoteMilestone,
  isMilestone,
} from '@monetize/types/app';
import { ApiListResponse, TDataTablePager } from '@monetize/types/common';
import orderBy from 'lodash/orderBy';
import {
  Dispatch,
  FunctionComponent as FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useCreateNote,
  useDeleteNote,
  useGetNotes,
  useUpdateNote,
} from '../../api/notesService';
import { useACL } from '../../services/acl/acl';
import {
  MBox,
  MButton,
  MCircularProgress,
  MDivider,
  MDrawer,
  MDrawerBody,
  MDrawerContent,
  MDrawerFooter,
  MDrawerHeader,
  MDrawerOverlay,
  MDrawerPositioner,
  MFlex,
  MText,
} from '../Monetize';
import { LoadMore } from '../Monetize/LoadMore';
import MEmptyDataPlaceholder from '../Monetize/MEmptyDataPlaceholder';
import { NoteForm } from './NoteForm';
import { NoteList } from './NoteList';

const NEW_NOTE_TEMP_KEY = '~~NEW~~';

interface NotesDrawerProps {
  id: string;
  entity: 'accounts' | 'quotes';
  milestones?: NoteMilestone[];
  isOpen: boolean;
  unsavedNotes: Map<string, INoteResSchema>;
  setUnsavedNotes: Dispatch<SetStateAction<Map<string, INoteResSchema>>>;
  setNotesWithoutMilestones: (notes?: ApiListResponse<INoteResSchema>) => void;
  onClose: () => void;
}

const pageSize = 20;

export const NotesDrawerV2: FC<NotesDrawerProps> = ({
  id,
  entity = 'accounts',
  milestones,
  isOpen,
  unsavedNotes,
  setUnsavedNotes,
  setNotesWithoutMilestones,
  onClose,
}: NotesDrawerProps) => {
  const noteBtnRef = useRef<any>(undefined);

  const [isCreateFormActive, setIsCreateFormActive] = useState(() =>
    unsavedNotes.has(NEW_NOTE_TEMP_KEY),
  );

  const { canDo } = useACL();
  const canAddNewNotes = canDo([['sales', 'update']]);

  const [pager, setPager] = useState<TDataTablePager>({
    first: 0,
    rows: pageSize,
    page: 0,
    sortField: 'createDate',
    sortOrder: -1,
  });

  const [notesBodyRef, setRefToNotesBody] = useState<HTMLDivElement | null>(
    null,
  );
  const notesBodyTopRef = useRef<HTMLDivElement | null>(null);
  const {
    data: notesWithoutMilestones,
    isLoading: fetchLoading,
    isFetching: fetchFetching,
  } = useGetNotes({ entityPath: entity, id, config: pager }, { enabled: !!id });

  useEffect(() => {
    if (notesWithoutMilestones) {
      setNotesWithoutMilestones(notesWithoutMilestones);
    }
  }, [notesWithoutMilestones]);

  const notes = useMemo(() => {
    if (notesWithoutMilestones?.content.length && milestones?.length) {
      return orderBy(
        [...notesWithoutMilestones.content, ...milestones],
        [
          (noteOrMilestone) =>
            isMilestone(noteOrMilestone)
              ? noteOrMilestone.date
              : noteOrMilestone.createDate,
        ],
        ['desc'],
      );
    }
    return notesWithoutMilestones?.content || [];
  }, [notesWithoutMilestones, milestones]);

  const { mutateAsync: createNote } = useCreateNote({ entityPath: entity, id });
  const { mutateAsync: updateNote } = useUpdateNote({ entityPath: entity, id });
  const { mutateAsync: deleteNote } = useDeleteNote({ entityPath: entity, id });

  /**
   * Save state of editing notes so that state can be restored if user closes drawer
   */
  const handleNoteModified = useCallback(
    (action: 'ADD' | 'CANCEL', note: INoteResSchema) => {
      setUnsavedNotes((prevNotes) => {
        const newNotes = new Map(prevNotes);
        if (action === 'CANCEL') {
          newNotes.delete(NEW_NOTE_TEMP_KEY);
        }
        if (action === 'ADD') {
          newNotes.set(note.id || NEW_NOTE_TEMP_KEY, note);
        }
        return newNotes;
      });
    },
    [setUnsavedNotes],
  );

  /**
   * Handle any mutation action
   * Will remove note from unsavedNotes if save is successful
   */
  const handleAction = async (
    action: 'CREATE' | 'UPDATE' | 'DELETE',
    note: INoteReqSchema,
  ) => {
    function removeFromUnsavedNotes(noteId: string) {
      setUnsavedNotes((prevNotes) => {
        const newNotes = new Map(prevNotes);
        newNotes.delete(noteId);
        return newNotes;
      });
    }
    switch (action) {
      case 'CREATE':
        return createNote(note).then((res) => {
          removeFromUnsavedNotes(NEW_NOTE_TEMP_KEY);
          setPager({
            ...pager,
            rows: pager.rows + 1,
          });
          return res;
        });
      case 'UPDATE':
        return updateNote(note).then((res) => {
          removeFromUnsavedNotes(note.id!);
          return res;
        });
      case 'DELETE':
        return deleteNote(note.id!).then((res) => {
          removeFromUnsavedNotes(note.id!);
          return res;
        });
      default:
        throw new Error('Invalid action');
    }
  };

  const handleCreateFormClose = () => {
    setIsCreateFormActive(false);
    if (unsavedNotes.has(NEW_NOTE_TEMP_KEY)) {
      handleNoteModified('CANCEL', unsavedNotes.get(NEW_NOTE_TEMP_KEY)!);
    }
  };

  const onCloseDrawer = () => {
    onClose();
    // If unsaved new note is blank, remove it from unsavedNotes
    if (unsavedNotes.has(NEW_NOTE_TEMP_KEY)) {
      const { subject, note } = unsavedNotes.get(NEW_NOTE_TEMP_KEY)!;
      if (!subject && !note) {
        setIsCreateFormActive(false);
        handleNoteModified('CANCEL', unsavedNotes.get(NEW_NOTE_TEMP_KEY)!);
      }
    }
  };

  const getNextPageSize = (): number => {
    let nextPageSize = pageSize;
    if (
      notesWithoutMilestones &&
      notesWithoutMilestones.totalElements -
        notesWithoutMilestones.content.length <
        1.3 * pageSize
    ) {
      nextPageSize = notesWithoutMilestones.totalElements;
    }
    return nextPageSize;
  };

  return (
    <MBox position="relative">
      {isOpen && (
        <MDrawer
          open={isOpen}
          placement="end"
          onOpenChange={(e) => {
            if (!e.open) {
              onCloseDrawer();
            }
          }}
          finalFocusEl={() => noteBtnRef.current}
          size="sm"
          preventScroll={false}
        >
          <Portal>
            <MDrawerOverlay />
            <MDrawerPositioner>
              <MDrawerContent>
                {/* Header is shorter than the theme default because it includes
                a button; keep theme px so the bottom border spans full width. */}
                <MDrawerHeader py="2.5">
                  <MFlex
                    w="full"
                    justifyContent="space-between"
                    alignItems="center"
                    minH={8}
                  >
                    <MText fontSize="inherit" fontWeight="inherit">
                      Notes
                    </MText>
                    {!isCreateFormActive && canAddNewNotes && (
                      <MButton
                        variant="secondary"
                        onClick={() => setIsCreateFormActive(true)}
                      >
                        New Note
                      </MButton>
                    )}
                  </MFlex>
                </MDrawerHeader>
                <MDrawerBody
                  ref={setRefToNotesBody}
                  px="2"
                  bgColor="tGray.sidebarDark"
                >
                  <MBox ref={notesBodyTopRef} height={1} />
                  {fetchLoading && !notes.length && (
                    <MFlex
                      w="full"
                      h="full"
                      justify="center"
                      alignItems="center"
                    >
                      <MCircularProgress isIndeterminate />
                    </MFlex>
                  )}
                  {!notes.length && !isCreateFormActive && (
                    <MFlex
                      flexDirection="column"
                      pt={2}
                      pb={4}
                      bgColor="tWhite.base"
                    >
                      <MEmptyDataPlaceholder
                        mainMessage="Looks like there are no notes here."
                        boxProps={{ height: '10vh' }}
                        mainMessageProps={{
                          fontSize: 'md',
                          fontWeight: 'semibold',
                        }}
                      />
                    </MFlex>
                  )}
                  {isCreateFormActive && (
                    <NoteForm
                      onClose={handleCreateFormClose}
                      createNote={(note) => handleAction('CREATE', note)}
                      updateNote={(note) => handleAction('UPDATE', note)}
                      handleNoteModified={handleNoteModified}
                      initialNote={unsavedNotes.get(NEW_NOTE_TEMP_KEY)}
                    />
                  )}

                  {!!notes.length && (
                    <>
                      <NoteList
                        notes={notes}
                        milestones={milestones}
                        handleAction={handleAction}
                        unsavedNotes={unsavedNotes}
                        handleNoteModified={handleNoteModified}
                        isReadOnly={!canAddNewNotes}
                      />
                      {notesWithoutMilestones?.content.length && (
                        <LoadMore
                          fetchedElementLength={
                            notesWithoutMilestones.content.length
                          }
                          totalElements={notesWithoutMilestones.totalElements}
                          isLoading={
                            fetchFetching &&
                            !!notesWithoutMilestones.content.length
                          }
                          containerElement={notesBodyRef}
                          scrollToTopElement={notesBodyTopRef.current}
                          onLoadMore={() => {
                            setPager({
                              ...pager,
                              rows: pager.rows + getNextPageSize(),
                            });
                          }}
                        />
                      )}
                    </>
                  )}
                </MDrawerBody>

                <MDivider />

                <MDrawerFooter>
                  <MBox w="full">
                    <MFlex justifyContent="center">
                      <MButton
                        mt={5}
                        variant="cancel"
                        justifySelf="center"
                        onClick={onCloseDrawer}
                      >
                        Close
                      </MButton>
                    </MFlex>
                  </MBox>
                </MDrawerFooter>
              </MDrawerContent>
            </MDrawerPositioner>
          </Portal>
        </MDrawer>
      )}
    </MBox>
  );
};
